<template>
  <div class="node-pools-table-body-row" aid="quota-tab-content">
    <!--  NODE POOL NAME -->
    <node-pool-name
      :is-only-default-node-pool="options.isOnlyDefaultNodePool"
      :node-pool-name="getNodePoolNameByResource(model.resource)"
    />

    <!--  OVER QUOTA -->
    <over-quota-button :resource="model.resource" />

    <!--  GPU -->
    <gpu-quota-input
      v-if="shouldUseExtendedQuotaInput(model.departmentDeservedResources.gpu)"
      :read-only="options.readOnly"
      :is-alert-on="isGpuAlertOn"
      :projects-deserved-gpu="model.projectsDeservedResources.gpu"
      :department-deserved-gpu="maxGpuInput"
      :allocated-non-preemptible-gpu="model.allocatedNonPreemptibleResources.gpu"
      :model-value="model.resource.gpu.deserved"
      :data-testid="`gpu-quota-button-${getNodePoolNameByResource(model.resource)}`"
      @update:model-value="updateNodePoolResource(EResourceType.GPU, $event)"
    />
    <gpu-quota-input
      v-else
      hide-slider
      :read-only="options.readOnly"
      :model-value="model.resource.gpu.deserved"
      :department-deserved-gpu="maxGpuInput"
      :data-testid="`gpu-quota-button-${getNodePoolNameByResource(model.resource)}`"
      @update:model-value="updateNodePoolResource(EResourceType.GPU, $event)"
    />

    <!--  CPU -->
    <template v-if="options.isCpuEnabled">
      <cpu-quota-input
        v-if="shouldUseExtendedQuotaInput(model.departmentDeservedResources.cpu)"
        :read-only="options.readOnly"
        :is-alert-on="isCpuAlertOn"
        :projects-deserved-cpu="model.projectsDeservedResources.cpu"
        :department-deserved-cpu="maxCpuCoresInput"
        :department-deserved-cpu-is-unlimited="isResourceUnlimited(model.departmentDeservedResources.cpu)"
        :allocated-non-preemptible-cpu="model.allocatedNonPreemptibleResources.cpu"
        :model-value="cpuInCores"
        :data-testid="`cpu-quota-button-${getNodePoolNameByResource(model.resource)}`"
        @update:model-value="updateNodePoolResource(EResourceType.CPU, $event)"
      />
      <cpu-quota-input
        v-else
        hide-slider
        :read-only="options.readOnly"
        :model-value="cpuInCores"
        :department-deserved-cpu-is-unlimited="isResourceUnlimited(model.departmentDeservedResources.cpu)"
        :data-testid="`cpu-quota-button-${getNodePoolNameByResource(model.resource)}`"
        @update:model-value="updateNodePoolResource(EResourceType.CPU, $event)"
      />

      <!--  MEMORY -->

      <memory-quota-input
        v-if="shouldUseExtendedQuotaInput(model.departmentDeservedResources.memory)"
        :read-only="options.readOnly"
        :is-alert-on="isMemoryAlertOn"
        :projects-deserved-memory="model.projectsDeservedResources.memory"
        :department-deserved-memory="maxCpuMemoryInput"
        :department-deserved-memory-is-unlimited="
          isProjectEntity ? isResourceUnlimited(model.departmentDeservedResources.memory) : undefined
        "
        :allocated-non-preemptible-memory="model.allocatedNonPreemptibleResources.memory"
        :model-value="model.resource?.memory?.deserved"
        :data-testid="`memory-quota-button-${getNodePoolNameByResource(model.resource)}`"
        @update:model-value="updateNodePoolResource(EResourceType.MEMORY, $event)"
      />

      <memory-quota-input
        v-else
        hide-slider
        :read-only="options.readOnly"
        :model-value="model.resource?.memory?.deserved"
        :department-deserved-memory-is-unlimited="isResourceUnlimited(model.departmentDeservedResources.memory)"
        :data-testid="`memory-quota-button-${getNodePoolNameByResource(model.resource)}`"
        @update:model-value="updateNodePoolResource(EResourceType.MEMORY, $event)"
      />
    </template>
  </div>
</template>
<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { MemoryQuotaInput } from "@/components/org-unit/quota-management/quota-inputs/cpu-memory-quota-input";
import { CpuQuotaInput } from "@/components/org-unit/quota-management/quota-inputs/cpu-quota-input";
import { GpuQuotaInput } from "@/components/org-unit/quota-management/quota-inputs/gpu-quota-input";
import { NodePoolName } from "@/components/org-unit/quota-management/node-pools-table/node-pool-row/noed-pool-name";
import type { Resources } from "@/swagger-models/org-unit-service-client";
import { orgUnitUtil } from "@/utils/org-unit.util";
import {
  CPU_VALUE_FACTOR,
  EQuotaEntity,
  EResourceType,
  MAX_PRESENTED_CPU_CORES,
  MAX_PRESENTED_CPU_MEMORY_MIB,
  MIN_PRESENTED_CPU_CORES,
  MIN_PRESENTED_CPU_MEMORY_MIB,
  MIN_PRESENTED_GPU,
} from "@/models/resource.model";
import { resourceUtil } from "@/utils/resource.util";
import { DEFAULT_DEPARTMENT_DESERVED_GPUS } from "@/models/department.model";
import type { NodePoolModel, NodePoolOptions } from "@/models/org-unit.model";
import { OverQuotaButton } from "@/components/org-unit/quota-management/quota-inputs/over-quota-button";

enum ENodePoolRowEmitName {
  UpdateNodePoolPriority = "update-node-pool-priority",
  UpdateNodePoolResource = "update-node-pool-resource",
  UpdateNodePoolOverQuota = "update-node-pool-over-quota",
}

export default defineComponent({
  name: "quota-tab-content",
  components: { OverQuotaButton, NodePoolName, GpuQuotaInput, CpuQuotaInput, MemoryQuotaInput },
  emits: {
    [ENodePoolRowEmitName.UpdateNodePoolPriority]: (payload: { value: string | number; name: string }) => payload,
    [ENodePoolRowEmitName.UpdateNodePoolResource]: (payload: {
      name: string;
      resourceType: EResourceType;
      resourceValue: number | null;
    }) => payload,
    [ENodePoolRowEmitName.UpdateNodePoolOverQuota]: (payload: { name: string; overQuota: number }) => payload,
  },
  inject: ["entity"],
  props: {
    options: {
      type: Object as PropType<NodePoolOptions>,
      required: true,
    },
    model: {
      type: Object as PropType<NodePoolModel>,
      required: true,
    },
  },
  computed: {
    isProjectEntity(): boolean {
      return this.entity === EQuotaEntity.project;
    },
    EQuotaEntity(): typeof EQuotaEntity {
      return EQuotaEntity;
    },
    EResourceType(): typeof EResourceType {
      return EResourceType;
    },
    cpuInCores(): number | null {
      if (this.model.resource?.cpu?.deserved === null || this.model.resource?.cpu?.deserved === undefined) {
        return null;
      }
      return this.model.resource?.cpu?.deserved / CPU_VALUE_FACTOR;
    },
    maxGpuInput(): number {
      if (this.isProjectEntity) {
        return this.model.departmentDeservedResources.gpu;
      }
      return Math.max(MIN_PRESENTED_GPU, this.model.totalClusterResources.gpu);
    },
    maxCpuCoresInput(): number {
      if (this.isProjectEntity) {
        return this.model.departmentDeservedResources.cpu;
      }
      const clusterCpuMilliCores = this.model.totalClusterResources.cpuCores * CPU_VALUE_FACTOR;
      if (MAX_PRESENTED_CPU_CORES * CPU_VALUE_FACTOR > clusterCpuMilliCores) {
        return Math.max(MIN_PRESENTED_CPU_CORES * CPU_VALUE_FACTOR, clusterCpuMilliCores);
      }
      return MAX_PRESENTED_CPU_CORES * CPU_VALUE_FACTOR;
    },
    maxCpuMemoryInput(): number {
      if (this.isProjectEntity) {
        return this.model.departmentDeservedResources.memory;
      }
      const clusterCpuMemoryMib = resourceUtil.fromBytesToMiB(this.model.totalClusterResources.memoryBytes);
      if (MAX_PRESENTED_CPU_MEMORY_MIB > clusterCpuMemoryMib) {
        return Math.max(MIN_PRESENTED_CPU_MEMORY_MIB, clusterCpuMemoryMib);
      }
      return MAX_PRESENTED_CPU_MEMORY_MIB;
    },
    isGpuAlertOn(): boolean {
      if (this.entity === EQuotaEntity.department) {
        return (
          this.model.resource.gpu.deserved < this.model.projectsDeservedResources.gpu &&
          this.model.resource.gpu.deserved !== DEFAULT_DEPARTMENT_DESERVED_GPUS
        );
      }
      return this.isResourceAlertOn(EResourceType.GPU, this.model.resource.gpu.deserved);
    },
    isCpuAlertOn(): boolean {
      if (this.entity === EQuotaEntity.department && this.model.resource?.cpu?.deserved) {
        return this.model.resource?.cpu?.deserved < this.model.projectsDeservedResources.cpu;
      }
      return this.isResourceAlertOn(EResourceType.CPU, this.model.resource?.cpu?.deserved);
    },
    isMemoryAlertOn(): boolean {
      if (this.entity === EQuotaEntity.department && this.model.resource?.cpu?.deserved) {
        return this.model.resource?.cpu?.deserved < this.model.projectsDeservedResources.cpu;
      }
      return this.isResourceAlertOn(EResourceType.MEMORY, this.model.resource?.memory?.deserved);
    },
  },
  methods: {
    updateNodePoolResource(resourceType: EResourceType, resourceValue: number | null): void {
      this.$emit(ENodePoolRowEmitName.UpdateNodePoolResource, {
        name: this.model.resource?.nodePool?.name || "",
        resourceType,
        resourceValue,
      });
    },
    isResourceUnlimited(resourceValue?: number | null): boolean {
      return resourceUtil.isResourceUnlimited(resourceValue);
    },
    isResourceUnderAllocatedNonPreemptible(
      resourceType: EResourceType,
      resourceValue: number | null | undefined,
    ): boolean {
      if (resourceValue === null || resourceValue === undefined) return false;
      switch (resourceType) {
        case EResourceType.GPU:
          return resourceUtil.isResourceUnderAllocatedNonPreemptible(
            resourceValue,
            this.model.allocatedNonPreemptibleResources.gpu,
          );
        case EResourceType.CPU:
          return resourceUtil.isResourceUnderAllocatedNonPreemptible(
            resourceValue / CPU_VALUE_FACTOR,
            this.model.allocatedNonPreemptibleResources.cpu / CPU_VALUE_FACTOR,
          );
        case EResourceType.MEMORY:
          return resourceUtil.isResourceUnderAllocatedNonPreemptible(
            resourceValue,
            this.model.allocatedNonPreemptibleResources.memory,
          );
        default:
          return false;
      }
    },
    isNodePoolResourceOverQuota(resourceType: EResourceType, resourceValue: number | null | undefined): boolean {
      if (this.entity === EQuotaEntity.department) return false;

      switch (resourceType) {
        case EResourceType.GPU:
          return this.model.isNodePoolOverQuota.gpu;
        case EResourceType.CPU:
          return (
            this.model.isNodePoolOverQuota.cpu ||
            (resourceValue === null && !this.isResourceUnlimited(this.model.departmentDeservedResources.cpu))
          );
        case EResourceType.MEMORY:
          return (
            this.model.isNodePoolOverQuota.memory ||
            (resourceValue === null && !this.isResourceUnlimited(this.model.departmentDeservedResources.memory))
          );
      }
    },
    isResourceAlertOn(resourceType: EResourceType, resourceValue: number | null | undefined): boolean {
      return (
        this.isResourceUnderAllocatedNonPreemptible(resourceType, resourceValue) ||
        this.isNodePoolResourceOverQuota(resourceType, resourceValue)
      );
    },
    getNodePoolNameByResource(resources: Resources): string {
      return orgUnitUtil.getNodePoolNameByResource(resources);
    },
    shouldUseExtendedQuotaInput(departmentResource?: number | null): boolean {
      if (!this.options.isDepartmentEnabled) {
        return false;
      }
      if (this.isProjectEntity) {
        return !this.isResourceUnlimited(departmentResource);
      }
      return true;
    },
  },
});
</script>

<style lang="scss" scoped>
.node-pools-table-body-row {
  gap: 8px;
  display: flex;
  align-items: center;
  width: 100%;
}
.node-pools-table-body-row > *:first-child {
  flex-grow: 1;
}
.node-pools-table-body-row > *:nth-child(n + 2) {
  flex-shrink: 0;
}
</style>
